// Data layer for the v2 planner. Talks to the SAME backend as v1:
//   GET   /:user/plans/:slug/data  -> Plan#data (version-specific JSON)
//   PATCH /:user/plans/:slug       -> save (plan[content] = v3)
// Migrates whatever the server returns up to v3 in memory so older plans render.

import { migrate } from './migrations';

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

export async function loadPlanData(dataUrl) {
  const response = await fetch(dataUrl, {
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
  });
  if (!response.ok) throw new Error(`Failed to load plan (${response.status})`);
  return migrate(await response.json());
}

const GENERIC_SAVE_ERROR = 'Something went wrong while saving. Please try again.';
// Cap the server text we keep so an HTML error page (e.g. a Cloudflare 5xx)
// cannot leak its body into the reported error.
const MAX_SERVER_MESSAGE = 300;

// A fetch that never reached the server: the connection dropped, the user
// navigated away mid-request, or something blocked the call. This is not a
// defect in our save logic, so callers report it apart from server errors.
class PlanNetworkError extends Error {
  constructor() {
    super('Check your connection and try again.');
    this.name = 'PlanNetworkError';
    this.isNetworkError = true;
  }
}

// A save the server rejected. Carries the HTTP status so callers can report it
// as an event property instead of baking it into the exception name.
class PlanSaveError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'PlanSaveError';
    this.status = status;
  }
}

// Send a plan via fetch (not p5.httpDo) so we can read validation errors —
// mirrors the pattern shipped for v1 in PR #88. Resolves to the parsed JSON on
// success. Throws PlanNetworkError on a network-level rejection, or
// PlanSaveError (with the server's reason and status) on a non-2xx response.
async function submitPlan(url, method, payload) {
  let response;
  try {
    response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify(payload),
    });
  } catch (_) {
    throw new PlanNetworkError();
  }
  if (!response.ok) {
    let message = GENERIC_SAVE_ERROR;
    try {
      const errors = await response.json();
      const parts = Object.entries(errors).map(([field, msgs]) => {
        const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
        return field === 'base' ? text : `${field} ${text}`;
      });
      if (parts.length) message = parts.join('. ');
    } catch (_) { /* non-JSON error body */ }
    throw new PlanSaveError(message.slice(0, MAX_SERVER_MESSAGE), response.status);
  }
  return response.json();
}

// Save an existing plan (PATCH /:user/plans/:slug).
export function savePlan(saveUrl, payload) {
  return submitPlan(saveUrl, 'PATCH', payload);
}

// Create a new plan (POST /plans). Returns the parsed JSON ({ uri, ... }).
export function createPlan(createUrl, payload) {
  return submitPlan(createUrl, 'POST', payload);
}

// LocalStorage draft — same key scheme as v1 so a draft survives across versions.
export function draftKey(username, slug) { return `plans.${username}.${slug}`; }

export function saveDraft(username, slug, content) {
  try { localStorage.setItem(draftKey(username, slug), JSON.stringify(content)); } catch (_) { /* quota */ }
}
export function loadDraft(username, slug) {
  try { const v = localStorage.getItem(draftKey(username, slug)); return v ? JSON.parse(v) : null; } catch (_) { return null; }
}
export function clearDraft(username, slug) {
  try { localStorage.removeItem(draftKey(username, slug)); } catch (_) { /* ignore */ }
}
